import { getDisplayName } from "../actions/utils/actionUtils";

type RequestChange = {
  field: string;
  oldValue?: any;
  newValue?: any;
};

const EMPTY_VALUES = new Set([
  "id",
  "uuid",
  "entityId",
  "entityType",
  "action",
  "status",
  "createdAt",
  "updatedAt",
  "requestedAt",
  "requestId",
  "parentId",
  "childId",
  "familyId",
]);

const OLD_KEYS = ["oldValue", "old", "previous", "before", "from"];
const NEW_KEYS = ["newValue", "new", "next", "after", "to", "value"];

export const parseRequestPayload = (payload: any) => {
  if (!payload) return null;
  if (typeof payload !== "string") return payload;

  try {
    return JSON.parse(payload);
  } catch {
    return null;
  }
};

const normalizeEntityType = (request: any) =>
  String(
    request?.entityType ||
      request?.targetType ||
      request?.type ||
      request?.requestType ||
      "",
  ).toUpperCase();

const normalizeAction = (request: any) =>
  String(request?.action || request?.mutationType || "").toUpperCase();

export const getRequestEntityLabel = (request: any) => {
  const entityType = normalizeEntityType(request);

  if (entityType === "CHILD") return "Child";
  if (entityType === "PARENT") return "Parent";
  if (entityType === "FAMILY") return "Family";
  if (entityType === "BANK_ACCOUNT") return "Bank account";

  return entityType || "Request";
};

export const getRequestActionLabel = (request: any) => {
  const entityType = normalizeEntityType(request);
  const action = normalizeAction(request);

  if (entityType === "BANK_ACCOUNT") return "Bank account change";
  if (action === "CREATE") return "Create";
  if (action === "UPDATE") return "Update";
  if (action === "DELETE") return "Delete";

  return action || "Change";
};

const findById = (items: any[] = [], id: any) => {
  if (id === null || id === undefined) return null;
  return items.find(
    (item) =>
      String(item?.id) === String(id) ||
      String(item?.uuid) === String(id) ||
      String(item?.entityId) === String(id),
  );
};

const findChildName = (childId: any, childrenList: any[] = []) => {
  const child = findById(childrenList, childId);
  return getDisplayName(child) || null;
};

const getPayloadEntity = (payload: any) =>
  payload?.child || payload?.family || payload?.parent || payload?.entity || payload;

export const getRequestTargetName = (
  request: any,
  childrenList: any[] = [],
  families: any[] = [],
  parentData: any = null,
) => {
  const payload = parseRequestPayload(request?.payload);
  const entityType = normalizeEntityType(request);
  const payloadEntity = getPayloadEntity(payload);

  const directName =
    request?.childName ||
    request?.targetName ||
    request?.displayName ||
    request?.name ||
    request?.title;
  if (directName) return String(directName);

  if (entityType === "CHILD") {
    const parsedName = getDisplayName(payloadEntity);
    if (parsedName) return parsedName;

    const childId =
      request?.entityId ||
      payloadEntity?.childId ||
      payloadEntity?.id ||
      payload?.childId ||
      payload?.id;
    const lookup = findChildName(childId, childrenList);
    if (lookup) return lookup;
  }

  if (entityType === "FAMILY") {
    const familyName =
      payload?.familyName ||
      payload?.family?.familyName ||
      payload?.name ||
      payloadEntity?.familyName;
    if (familyName) return String(familyName);

    const family = findById(families, request?.entityId || payload?.familyId);
    if (family?.familyName) return String(family.familyName);
    if (family?.name) return String(family.name);
  }

  if (entityType === "BANK_ACCOUNT" || entityType === "PARENT") {
    const parentName = getDisplayName(parentData) || parentData?.email;
    if (parentName) return String(parentName);
  }

  const parsedName = getDisplayName(payloadEntity);
  if (parsedName) return parsedName;
  if (request?.entityId) return `#${request.entityId}`;

  return "Details pending";
};

const isPlainObject = (value: any) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const prettifyField = (field: string) =>
  String(field || "")
    .replace(/^payload\./, "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[._-]+/g, " ")
    .trim();

const pushUnique = (changes: RequestChange[], change: RequestChange) => {
  const field = prettifyField(change.field);
  if (!field || EMPTY_VALUES.has(change.field)) return;

  const exists = changes.some(
    (item) =>
      item.field === field &&
      item.oldValue === change.oldValue &&
      item.newValue === change.newValue,
  );
  if (!exists) changes.push({ ...change, field });
};

const objectDiff = (before: any, after: any) => {
  const changes: RequestChange[] = [];
  const keys = new Set([
    ...Object.keys(isPlainObject(before) ? before : {}),
    ...Object.keys(isPlainObject(after) ? after : {}),
  ]);

  keys.forEach((key) => {
    if (EMPTY_VALUES.has(key)) return;
    pushUnique(changes, {
      field: key,
      oldValue: before?.[key],
      newValue: after?.[key],
    });
  });

  return changes;
};

const changesFromArray = (items: any[]) => {
  const changes: RequestChange[] = [];

  items.forEach((item, index) => {
    if (!item) return;
    if (typeof item === "string") {
      pushUnique(changes, { field: item, newValue: undefined });
      return;
    }

    const field =
      item.field || item.name || item.key || item.path || item.property || `Field ${index + 1}`;
    const oldKey = OLD_KEYS.find((key) => Object.prototype.hasOwnProperty.call(item, key));
    const newKey = NEW_KEYS.find((key) => Object.prototype.hasOwnProperty.call(item, key));

    pushUnique(changes, {
      field,
      oldValue: oldKey ? item[oldKey] : undefined,
      newValue: newKey ? item[newKey] : undefined,
    });
  });

  return changes;
};

const changesFromMap = (fields: any) => {
  const changes: RequestChange[] = [];
  if (!isPlainObject(fields)) return changes;

  Object.entries(fields).forEach(([field, value]) => {
    if (EMPTY_VALUES.has(field)) return;

    if (isPlainObject(value)) {
      const oldKey = OLD_KEYS.find((key) => Object.prototype.hasOwnProperty.call(value, key));
      const newKey = NEW_KEYS.find((key) => Object.prototype.hasOwnProperty.call(value, key));
      pushUnique(changes, {
        field,
        oldValue: oldKey ? (value as any)[oldKey] : undefined,
        newValue: newKey ? (value as any)[newKey] : value,
      });
      return;
    }

    pushUnique(changes, { field, newValue: value });
  });

  return changes;
};

const bankAccountChanges = (request: any, payload: any) => {
  if (normalizeEntityType(request) !== "BANK_ACCOUNT") return [];

  const source = payload?.bankAccount || payload?.account || payload || {};
  const changes: RequestChange[] = [];
  const iban =
    source.iban ||
    source.newIban ||
    source.bankAccountNumber ||
    source.newBankAccountNumber ||
    source.accountNumber;
  const oldIban =
    source.oldIban ||
    source.previousIban ||
    source.oldBankAccountNumber ||
    source.previousBankAccountNumber;
  const holder =
    source.accountHolder ||
    source.accountHolderName ||
    source.newAccountHolder ||
    source.name;
  const oldHolder = source.oldAccountHolder || source.previousAccountHolder;

  if (oldIban !== undefined || iban !== undefined) {
    pushUnique(changes, {
      field: "IBAN / bank account number",
      oldValue: oldIban,
      newValue: iban,
    });
  }
  if (oldHolder !== undefined || holder !== undefined) {
    pushUnique(changes, {
      field: "Account holder",
      oldValue: oldHolder,
      newValue: holder,
    });
  }

  return changes;
};

export const getRequestChangedFields = (request: any) => {
  const payload = parseRequestPayload(request?.payload);
  if (!payload || !isPlainObject(payload)) return [];

  const changes: RequestChange[] = [];
  bankAccountChanges(request, payload).forEach((change) => pushUnique(changes, change));

  [payload.changes, payload.changedFields, payload.fields].forEach((fieldSet) => {
    if (Array.isArray(fieldSet)) {
      changesFromArray(fieldSet).forEach((change) => pushUnique(changes, change));
    } else if (isPlainObject(fieldSet)) {
      changesFromMap(fieldSet).forEach((change) => pushUnique(changes, change));
    }
  });

  [
    [payload.oldValues, payload.newValues],
    [payload.previous, payload.next],
    [payload.before, payload.after],
  ].forEach(([before, after]) => {
    objectDiff(before, after).forEach((change) => pushUnique(changes, change));
  });

  if (changes.length > 0) return changes;

  const flatSource = getPayloadEntity(payload);
  if (!isPlainObject(flatSource)) return [];

  Object.entries(flatSource).forEach(([field, value]) => {
    if (EMPTY_VALUES.has(field) || isPlainObject(value) || Array.isArray(value)) return;
    pushUnique(changes, { field, newValue: value });
  });

  return changes;
};

const maskIban = (value: string) => {
  const compact = value.replace(/\s+/g, "");
  if (compact.length <= 8) return value;
  return `${compact.slice(0, 4)} **** **** ${compact.slice(-4)}`;
};

const maskBsn = (value: string) => {
  const compact = value.replace(/\s+/g, "");
  if (compact.length <= 4) return "****";
  return `*****${compact.slice(-4)}`;
};

export const formatRequestValue = (value: any, field = "") => {
  if (value === null || value === undefined || value === "") return "Not available";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) return value.map((item) => formatRequestValue(item)).join(", ");

  if (isPlainObject(value)) {
    return Object.entries(value)
      .filter(([key]) => !EMPTY_VALUES.has(key))
      .map(([key, item]) => `${prettifyField(key)}: ${formatRequestValue(item, key)}`)
      .join(", ");
  }

  const text = String(value);
  const normalizedField = String(field).toLowerCase();

  if (normalizedField.includes("bsn")) return maskBsn(text);
  if (
    normalizedField.includes("iban") ||
    normalizedField.includes("bank account") ||
    normalizedField.includes("account number")
  ) {
    return maskIban(text);
  }

  return text;
};

